import { useState, useEffect } from "react";
import { UserCheck, Check, X, MessageSquare, Lock, Server, Bot, Clock, DollarSign, ShieldAlert } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { useExecution } from "@/store/executionStore";
import { useHive } from "@/store/useHive";
import { RiskBadge } from "@/components/hive/shared";
import { RISK_META } from "@/components/hive/shared";

const RISK_EXPLAIN: Record<string, string> = {
  low: "Routine action with minimal blast radius. Reversible and audited.",
  medium: "Moderate impact — touches shared systems. Reviewed and reversible.",
  high: "High impact — external surface or budget spend. Verify before approving.",
  restricted: "Restricted — infrastructure or production change. Requires admin sign-off and is fully audited.",
};

export function ApprovalModal() {
  const id = useExecution((s) => s.approvalModalId);
  const approvals = useExecution((s) => s.approvals);
  const executions = useExecution((s) => s.executions);
  const close = useExecution((s) => s.openApprovalModal);
  const approve = useExecution((s) => s.approve);
  const reject = useExecution((s) => s.reject);
  const escalate = useExecution((s) => s.escalate);
  const pushToast = useHive((s) => s.pushToast);

  const a = approvals.find((x) => x.id === id) ?? null;
  const exec = a ? executions.find((e) => e.id === a.executionId) : null;
  const [reason, setReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  useEffect(() => {
    setReason("");
    setRejecting(false);
  }, [id]);

  if (!a) return null;
  const risk = RISK_META[a.risk];

  return (
    <Modal open={!!id} onClose={() => close(null)} title={a.action} subtitle={a.workflowName} icon={<UserCheck size={16} />} accent={risk.color} width={520}>
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <RiskBadge risk={a.risk} />
          <span className="flex items-center gap-1 text-[11px] text-app-2"><Server size={11} /> {a.system}</span>
          <span className="flex items-center gap-1 text-[11px] text-app-2"><Bot size={11} /> {a.requester}</span>
          <span className="ml-auto flex items-center gap-1 text-[11px] text-app-2"><Clock size={11} /> {a.timeout}</span>
        </div>

        {/* preview / diff */}
        <div>
          <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-app-2"><MessageSquare size={12} /> Change preview</div>
          <pre className="overflow-x-auto whitespace-pre-wrap rounded-xl border border-app bg-surface-2/50 px-3 py-2.5 font-mono text-[11px] leading-relaxed text-app-1">{a.preview}</pre>
        </div>

        {/* risk explanation */}
        <div className="flex items-start gap-2 rounded-xl border p-3 text-[11px]" style={{ borderColor: `${risk.color}44`, background: `${risk.color}0d`, color: risk.color }}>
          <ShieldAlert size={14} className="mt-0.5 shrink-0" />
          <div><span className="font-semibold">{risk.label} risk · </span><span className="opacity-90">{RISK_EXPLAIN[a.risk]}</span></div>
        </div>

        {/* permissions + cost */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-app bg-surface-2/40 p-2.5">
            <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-app-2"><Lock size={11} /> Permissions used</div>
            <div className="flex flex-wrap gap-1">
              {(exec?.permissionsUsed ?? ["—"]).map((p) => (
                <span key={p} className="rounded border border-app bg-surface px-1.5 py-0.5 font-mono text-[9px] text-app-1">{p}</span>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-app bg-surface-2/40 p-2.5">
            <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-app-2"><DollarSign size={11} /> Cost impact</div>
            <div className="text-lg font-semibold tabular-nums text-app">{a.costImpact > 0 ? `+$${a.costImpact.toFixed(2)}` : "$0.00"}</div>
            <div className="text-[10px] text-app-2">{a.costImpact > 0 ? "above per-run cap" : "within budget"}</div>
          </div>
        </div>

        {rejecting && (
          <input
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for rejection…"
            className="w-full rounded-lg border border-rose-500/40 bg-surface-2 px-3 py-2 text-xs text-app outline-none placeholder:text-app-2"
          />
        )}

        <div className="flex items-center gap-2 pt-1">
          <button
            onClick={() => { pushToast({ title: "Changes requested", body: a.action, tone: "info" }); close(null); }}
            className="flex items-center gap-1.5 rounded-lg border border-app bg-surface-2/60 px-3 py-2 text-xs font-medium text-app-1 transition hover:border-app-strong"
          >
            <MessageSquare size={13} /> Request changes
          </button>
          <button onClick={() => escalate(a.id)} className="rounded-lg border border-app bg-surface-2/60 px-3 py-2 text-xs font-medium text-app-1 transition hover:border-app-strong">Escalate</button>
          <div className="ml-auto flex items-center gap-2">
            {rejecting ? (
              <button onClick={() => reject(a.id, reason)} className="flex items-center gap-1.5 rounded-lg bg-rose-500 px-3.5 py-2 text-xs font-semibold text-white transition hover:brightness-110"><X size={13} /> Confirm reject</button>
            ) : (
              <button onClick={() => setRejecting(true)} className="flex items-center gap-1.5 rounded-lg bg-rose-500/15 px-3 py-2 text-xs font-semibold text-rose-500 transition hover:bg-rose-500/25"><X size={13} /> Reject</button>
            )}
            <button onClick={() => approve(a.id)} className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3.5 py-2 text-xs font-semibold text-white transition hover:brightness-110"><Check size={13} /> Approve</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
